<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.create" element="inertia-link">
                {{ $t('messages.create') }}
            </AppButton>
            <AppButton :href="meta.links.create_batch" element="inertia-link">
                {{ $t('messages.create_multiple') }}
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div :class="options.school_years.length > 1 ? 'sm:col-span-9' : 'sm:col-span-12'">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            :placeholder="`${$t('messages.search')}: ${$t('messages.title')}`"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-3" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        <FormSelect
                            id="trimester"
                            v-model="form.trimester"
                            :options="options.trimesters"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.trimester" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.grade_levels', 2)" for="grade_level_ids" />
                        <SlideOverCheckboxInput v-model="form.grade_level_ids" :options="options.grade_levels" />
                        <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 2)" for="subject_ids" />
                        <div v-if="filteredSubjects.length === 0">
                            {{ $t('messages.select') }}: {{ $tc('messages.grade_levels', 1) }}
                        </div>
                        <SlideOverCheckboxInput
                            v-model="form.subject_ids"
                            :options="filteredSubjects"
                            v-show="filteredSubjects.length > 0"
                        />
                        <FormError :value="form.errors.subject_ids" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.show_activities_that') }}
                    </label>
                    <FormSelect
                        id="teachesOrCreated"
                        v-model="form.teachesOrCreated"
                        :options="options.teachesOrCreated"
                        class="mt-1 w-full"
                        :null-option="false"
                    />
                    <FormError :value="form.errors.teachesOrCreated" class="mt-2" />
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.gradable')" for="gradable" />
                        <FormSelect
                            required
                            id="gradable"
                            v-model="form.gradable"
                            :options="options.gradables"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.gradable" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.graded')" for="graded" />
                        <FormSelect
                            id="gradable"
                            v-model="form.graded"
                            :options="options.graded"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.gradable" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.type')" for="type" />
                        <FormSelect
                            id="type"
                            v-model="form.type"
                            :options="options.types"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.gradable" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 flex justify-end">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="activities.data.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.grade_level') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.subject', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.dimensions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(activity, index) in activities.data"
                                :key="activity.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5">
                                    <div class="flex items-center space-x-1">
                                        <ActivityIcon :type="activity.icon_type" :title="activity.type_display" />
                                        <inertia-link :href="activity.links.show" class="text-indigo-500">
                                            {{ activity.title }}
                                        </inertia-link>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ activity.grade_level }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ activity.subject }}
                                </td>
                                <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                    <template v-if="activity.dimensionCategories">
                                        <div v-for="category in activity.dimensionCategories" :key="category.id">
                                            {{ category.name }}:
                                            <span
                                                v-for="dimension in category.dimensions"
                                                :title="dimension.title"
                                                :key="dimension.id"
                                                class="mr-1"
                                            >
                                                {{ dimension.abbreviation }}
                                            </span>
                                        </div>
                                    </template>
                                    <template v-if="activity.needs_evaluation_values">
                                        <Tippy>
                                            <InertiaLink :href="activity.links.needs_evaluation_values_route">
                                                <BadgePill color="red">
                                                    {{ $tc('messages.unfinished', 2) }}
                                                </BadgePill>
                                            </InertiaLink>

                                            <template #content>
                                                {{ activity.needs_evaluation_values_message }}
                                            </template>
                                        </Tippy>
                                    </template>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <OptionsPanel>
                                        <InertiaLink
                                            :href="activity.links.duplicate"
                                            method="post"
                                            as="button"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                        >
                                            <svg
                                                class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z"
                                                />
                                                <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                                            </svg>
                                            {{ $t('messages.duplicate') }}
                                        </InertiaLink>
                                        <InertiaLink
                                            v-if="activity.links.activity_questions_edit"
                                            :href="activity.links.activity_questions_edit"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                            role="menuitem"
                                        >
                                            <IconList class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                                            {{ $tc('messages.questions', 2) }}
                                        </InertiaLink>

                                        <template v-for="group in activity.links.groupsToGrade" :key="group.name">
                                            <a
                                                :href="group.links.grade"
                                                class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                role="menuitem"
                                            >
                                                <IconEdit
                                                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                />
                                                {{ $t('messages.add_scores') }}:
                                                {{ group.name }}
                                            </a>
                                        </template>
                                    </OptionsPanel>
                                    <InertiaSquareLinkButton action="show" :href="activity.links.show" />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="activity.links.edit"
                                        v-if="activity.can.update"
                                    />
                                    <InertiaSquareConfirmActionButton
                                        :href="activity.links.delete"
                                        danger
                                        :title="$t('messages.are_you_sure?')"
                                        :confirm-text="$t('messages.delete')"
                                        method="delete"
                                        v-if="activity.can.delete"
                                    >
                                        <template v-slot:icon>
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </template>

                                        <div class="flex flex-col">
                                            <AlertMessage
                                                class="mt-5 inline-block"
                                                v-if="activity.confirm_deletion_message"
                                            >
                                                <template v-slot:title>
                                                    {{ $t('messages.warning') }}
                                                </template>
                                                <template v-slot:body>
                                                    {{ activity.confirm_deletion_message }}
                                                </template>
                                            </AlertMessage>

                                            <AlertMessage class="mt-5 inline-block" v-if="activity.sessions_count">
                                                <template v-slot:title>
                                                    {{ $t('messages.warning') }}
                                                </template>
                                                <template v-slot:body>
                                                    {{ $t('messages.activity_in_programming_deletion_warning') }}
                                                </template>
                                            </AlertMessage>
                                        </div>
                                    </InertiaSquareConfirmActionButton>
                                    <InertiaSquareConfirmActionButton
                                        :href="activity.links.delete_questions_with_activity"
                                        danger
                                        :title="$t('messages.are_you_sure?')"
                                        :confirm-text="$t('messages.delete')"
                                        method="delete"
                                        v-if="meta.can.delete_questions_with_activity"
                                    >
                                        <template v-slot:icon>
                                            <IconSkull class="w-4 h-4" />
                                        </template>

                                        <div>{{ $tc('messages.questions', 2) }}: {{ activity.questions_count }}</div>
                                    </InertiaSquareConfirmActionButton>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="activities" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import OptionsPanel from '@/components/OptionsPanel.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import ActivityIcon from '@/components/ActivityIcon.vue';
import AppButton from '@/components/AppButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import { Tippy } from 'vue-tippy';
import { IconEdit, IconList, IconSkull } from '@tabler/icons-vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        SlideOverCheckboxInput,
        Tippy,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        FormLabel,
        FormError,
        FormSelect,
        OptionsPanel,
        EmptyState,
        BadgePill,
        ActivityIcon,
        AppButton,
        AlertMessage,
        InertiaSquareConfirmActionButton,
        IconList,
        IconSkull,
        IconEdit,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'activities', 'filters', 'defaults', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                trimester: this.filters.trimester,
                subject_ids: this.filters.subject_ids.map(Number),
                grade_level_ids: this.filters.grade_level_ids.map(Number),
                school_year_id: this.filters.school_year_id,
                teachesOrCreated: this.filters.teachesOrCreated,
                gradable: this.filters.gradable,
                type: this.filters.type,
                graded: this.filters.graded,
            }),
        };
    },
    computed: {
        stage_ids: function () {
            return this.options.grade_levels
                .filter((g) => this.form.grade_level_ids.includes(g.value))
                .map((g) => g.stage_id);
        },
        filteredSubjects: function () {
            return this.options.subjects.filter((s) => this.stage_ids.includes(s.stage_id));
        },
    },
    watch: {
        stage_ids: function (newVal) {
            this.form.subject_ids = this.options.subjects
                .filter((s) => newVal.includes(s.stage_id))
                .filter((s) => this.form.subject_ids.includes(s.value))
                .map((s) => s.value);
        },
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
